/**
 * FORFEO A3 UX5 — runtime essentiel (version allégée 5-A).
 *
 * Ce runtime ne gère plus QUE deux comportements robustes et testés :
 * - normalisation des URLs Explorer Plus (dédoublonnage/réparation des query strings);
 * - scroll interne du menu « Espace connecté ».
 *
 * L'icône Forfeo Reserve du header et la lisibilité mobile du hero sont désormais
 * portées AU SOURCE (Header.tsx / Home.tsx). Il n'y a plus d'injection d'icône ni de
 * suppression de bannière par heuristique DOM.
 *
 * Aucun rôle, prix, disponibilité, capacité ou état financier n'est résolu ici.
 */

const MARKER = '__forfeoA3EssentialRuntimeStarted';

const VALID_EXPLORER_CATEGORIES = new Set([
  'hebergements_locaux',
  'monde',
  'autos',
  'experiences',
  'esim',
]);

declare global {
  interface Window {
    [MARKER]?: boolean;
  }
}

function textOf(element: Element): string {
  return (element.textContent || '').replace(/\s+/g, ' ').trim();
}

function visible(element: Element): boolean {
  if (!(element instanceof HTMLElement)) return false;

  const rect = element.getBoundingClientRect();
  const style = window.getComputedStyle(element);

  return (
    rect.width > 0 &&
    rect.height > 0 &&
    style.display !== 'none' &&
    style.visibility !== 'hidden'
  );
}

function packedExplorerSearch(input: string): string {
  return input
    .replace(/(country=[^&?#]*?)(category=)/gi, '$1&$2')
    .replace(/([?&])category=([a-z0-9_-]+)category=/gi, '$1category=$2&category=')
    .replace(/&&+/g, '&');
}

function normalizeExplorerUrl(rawUrl: string): string {
  const url = new URL(rawUrl, window.location.origin);

  if (!url.pathname.startsWith('/explorer-plus')) {
    return rawUrl;
  }

  const packedSearch = packedExplorerSearch(url.search);
  const params = new URLSearchParams(packedSearch.startsWith('?') ? packedSearch.slice(1) : packedSearch);

  const categories = params
    .getAll('category')
    .map((value) => value.trim())
    .filter((value) => VALID_EXPLORER_CATEGORIES.has(value));

  if (categories.length > 0) {
    params.delete('category');
    params.set('category', categories[categories.length - 1]);
  }

  for (const key of ['pickup', 'origin', 'to', 'q', 'country']) {
    const value = params.get(key);

    if (value && /category=/i.test(value)) {
      const fixed = value.replace(/category=.*/i, '').trim();
      if (fixed) params.set(key, fixed);
    }
  }

  url.search = params.toString();

  if (rawUrl.startsWith('http://') || rawUrl.startsWith('https://')) {
    return url.toString();
  }

  return `${url.pathname}${url.search}${url.hash}`;
}

function normalizeCurrentExplorerPlusLocation(): void {
  if (!window.location.pathname.startsWith('/explorer-plus')) return;

  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const normalized = normalizeExplorerUrl(current);

  if (normalized !== current) {
    window.history.replaceState(window.history.state, document.title, normalized);
  }
}

function normalizeExplorerPlusAnchors(): void {
  for (const anchor of Array.from(document.querySelectorAll<HTMLAnchorElement>('a[href]'))) {
    const href = anchor.getAttribute('href') || '';

    if (!href.includes('/explorer-plus')) continue;

    const normalized = normalizeExplorerUrl(href);

    if (normalized !== href) {
      anchor.setAttribute('href', normalized);
    }

    const finalHref = anchor.getAttribute('href') || '';

    if (/^https?:\/\//i.test(finalHref) && !anchor.getAttribute('target')) {
      anchor.setAttribute('target', '_blank');
      anchor.setAttribute('rel', 'noopener noreferrer');
    }
  }
}

function installExplorerPlusDataLinkFallback(): void {
  if (document.body.dataset.forfeoA3ExplorerDataLinkFallback === 'installed') return;

  document.body.dataset.forfeoA3ExplorerDataLinkFallback = 'installed';

  document.addEventListener('click', (event) => {
    if (!window.location.pathname.startsWith('/explorer-plus')) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    const action = target.closest<HTMLElement>('[data-href], [data-url], [data-link], [data-target-url]');
    if (!action || action.closest('a[href]')) return;

    const rawUrl =
      action.getAttribute('data-href') ||
      action.getAttribute('data-url') ||
      action.getAttribute('data-link') ||
      action.getAttribute('data-target-url');

    if (!rawUrl || rawUrl === '#') return;

    const normalized = normalizeExplorerUrl(rawUrl);

    event.preventDefault();

    if (/^https?:\/\//i.test(normalized)) {
      window.open(normalized, '_blank', 'noopener,noreferrer');
      return;
    }

    window.location.assign(normalized);
  });
}

function findConnectedMenuPanel(from: Element): HTMLElement | null {
  let current: Element | null = from;

  for (let depth = 0; current && depth < 8; depth += 1) {
    if (current instanceof HTMLElement && visible(current)) {
      const rect = current.getBoundingClientRect();
      const style = window.getComputedStyle(current);

      const looksLikeAccountPanel =
        rect.width >= 260 &&
        rect.width <= 470 &&
        rect.height >= 220 &&
        rect.height <= window.innerHeight &&
        (
          style.position === 'fixed' ||
          style.position === 'absolute' ||
          rect.left > window.innerWidth * 0.35
        );

      if (looksLikeAccountPanel) return current;
    }

    if (current.parentElement === document.body) break;
    current = current.parentElement;
  }

  return null;
}

function enhanceConnectedMenuScroll(): void {
  for (const existing of Array.from(document.querySelectorAll<HTMLElement>('.forfeo-account-menu-scroll-essential'))) {
    const rect = existing.getBoundingClientRect();

    if (rect.width > 500 || rect.height > window.innerHeight) {
      existing.classList.remove('forfeo-account-menu-scroll-essential');
    }
  }

  const candidates = Array.from(document.querySelectorAll('body *')).filter((element) => {
    if (!visible(element)) return false;

    const text = textOf(element);

    return (
      text.includes('ESPACE CONNECTÉ') ||
      text.includes('MES PORTAILS FORFEO') ||
      text.includes('Mes portails Forfeo')
    );
  });

  for (const candidate of candidates) {
    const panel = findConnectedMenuPanel(candidate);

    if (panel) {
      panel.classList.add('forfeo-account-menu-scroll-essential');
    }
  }
}

function applyEssentialFixes(): void {
  normalizeCurrentExplorerPlusLocation();
  normalizeExplorerPlusAnchors();
  enhanceConnectedMenuScroll();
}

export function initForfeoA3EssentialRuntime(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (window[MARKER]) return;

  window[MARKER] = true;

  const run = () => {
    try {
      applyEssentialFixes();
    } catch (error) {
      console.warn('[FORFEO_A3_ESSENTIAL_RUNTIME_FAILED]', error);
    }
  };

  installExplorerPlusDataLinkFallback();

  run();

  window.addEventListener('resize', run);
  window.addEventListener('popstate', run);
  window.addEventListener('hashchange', run);

  const observer = new MutationObserver(() => {
    window.requestAnimationFrame(run);
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
}
